import { z } from 'zod';

import { type KnackField } from '@/types/schema/KnackField';
import { getAddressSchema } from '@/components/data-table/display/fields/address/AddressSchema';
import { getCurrencySchema } from '@/components/data-table/display/fields/currency/CurrencySchema';
import { getEmailSchema } from '@/components/data-table/display/fields/email/EmailSchema';
import { getFileSchema } from '@/components/data-table/display/fields/file/FileSchema';
import { getImageSchema } from '@/components/data-table/display/fields/image/ImageSchema';
import { getLinkSchema } from '@/components/data-table/display/fields/link/LinkSchema';
import { getNameSchema } from '@/components/data-table/display/fields/name/NameSchema';
import { getNumberSchema } from '@/components/data-table/display/fields/number/NumberSchema';
import { getParagraphTextSchema } from '@/components/data-table/display/fields/paragraph-text/ParagraphTextSchema';
import { getRichTextSchema } from '@/components/data-table/display/fields/rich-text/RichTextSchema';
import { getShortTextSchema } from '@/components/data-table/display/fields/short-text/ShortTextSchema';
import { type DataTableRowValue } from '@/components/data-table/display/types';
import { getDefaultValue } from '@/components/import/ConfirmImport';

export const getDefaultValues = (
  record: { [key: string]: DataTableRowValue },
  fields: KnackField[]
) =>
  Object.fromEntries(
    Object.entries(record).map(([key, field]) => {
      const currentField = fields.find((f) => f.key === key);

      if (!currentField) return [key, field.rawValue];
      if (currentField && currentField.type === 'timer' && field.rawValue) {
        const transformTimeValues = (timeObject: { hours: string; minutes: string }) => ({
          ...timeObject,
          hours: Number(timeObject.hours),
          minutes: Number(timeObject.minutes),
          rawTime: `${timeObject.hours}:${timeObject.minutes}`
        });

        const rawValueWithTimes = field.rawValue as {
          times: Array<{
            from: { hours: string; minutes: string };
            to: { hours: string; minutes: string };
          }>;
        };

        return [
          key,
          {
            ...rawValueWithTimes,
            times: rawValueWithTimes.times.map((time) => ({
              from: transformTimeValues(time.from),
              to: transformTimeValues(time.to)
            }))
          }
        ];
      }

      return [key, field.rawValue || getDefaultValue(currentField)];
    })
  );
export const generateFormSchema = (
  record: {
    [key: string]: DataTableRowValue;
  },
  knackFields: KnackField[]
) => {
  const schemaFields = {};

  Object.entries(record).forEach(([key]) => {
    const field = knackFields.find((f) => f.key === key);
    if (!field) return;
    if (field.type === 'short_text') {
      schemaFields[key] = getShortTextSchema(field).shape.shortText;
    } else if (field.type === 'number') {
      schemaFields[key] = getNumberSchema(field).shape.number;
    } else if (field.type === 'paragraph_text') {
      schemaFields[key] = getParagraphTextSchema(field).shape.paragraph;
    } else if (field.type === 'rich_text') {
      schemaFields[key] = getRichTextSchema(field).shape.richText;
    } else if (field.type === 'name') {
      schemaFields[key] = getNameSchema(field);
    } else if (field.type === 'link') {
      schemaFields[key] = getLinkSchema(field);
    } else if (field.type === 'address') {
      schemaFields[key] = getAddressSchema(field);
    } else if (field.type === 'currency') {
      schemaFields[key] = getCurrencySchema(field).shape.currency;
    } else if (field.type === 'email') {
      schemaFields[key] = getEmailSchema(field);
    } else if (field.type === 'rating') {
      schemaFields[key] = z.union([z.number(), z.string().length(0)]);
    } else if (field.type === 'file') {
      schemaFields[key] = getFileSchema(field);
    } else if (field.type === 'image') {
      schemaFields[key] = getImageSchema(field);
    } else {
      schemaFields[key] = z.any();
    }
  });

  return z.object(schemaFields);
};
